import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Download, Users, Mail, Phone, Globe, 
  Building, Linkedin, MapPin, User, CheckCircle2
} from 'lucide-react';
import { getUser } from '../lib/auth';
import { downloadGroupContactVCard } from '../lib/contactDownload';
import ContactDownloadModal from '../components/ContactDownloadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GroupView = () => {
  const { groupId } = useParams();
  const currentUser = getUser();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [visitorInfo, setVisitorInfo] = useState(null);

  useEffect(() => {
    loadGroup();
    // Check if visitor info already stored for this group session
    const storedInfo = sessionStorage.getItem(`visitor_info_${groupId}`);
    if (storedInfo) {
      try {
        setVisitorInfo(JSON.parse(storedInfo));
      } catch (e) {
        console.error('Failed to parse stored visitor info:', e);
      }
    }
  }, [groupId]);

  const loadGroup = async () => {
    try {
      const response = await axios.get(`${API}/contact-groups/${groupId}`);
      setGroup(response.data.group);
    } catch (error) {
      toast.error('Failed to load group');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVCard = async (contactIndex, contactName, visitorInfoParam = null) => {
    setDownloadingIndex(contactIndex);
    try {
      // Check if user is logged in OR if we have stored visitor info
      const infoToUse = visitorInfoParam || visitorInfo;
      
      if (!currentUser && !infoToUse) {
        // No user logged in and no visitor info stored - show modal
        setPendingDownload({ contactIndex, contactName });
        setShowDownloadModal(true);
        setDownloadingIndex(null);
        return;
      }

      // Download with tracking (use stored visitor info if available)
      await downloadGroupContactVCard(groupId, contactIndex, group.userId, infoToUse);
      toast.success(`${contactName}'s vCard downloaded! Email sent to ${contactName}.`);
    } catch (error) {
      toast.error('Failed to download vCard');
      console.error(error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleModalSubmit = async (submittedVisitorInfo) => {
    if (pendingDownload) {
      // Store visitor info for this group session
      setVisitorInfo(submittedVisitorInfo);
      sessionStorage.setItem(`visitor_info_${groupId}`, JSON.stringify(submittedVisitorInfo));
      
      // Download with the submitted info
      await handleDownloadVCard(pendingDownload.contactIndex, pendingDownload.contactName, submittedVisitorInfo);
      setPendingDownload(null);
      
      toast.success('Your info saved! You can now download other contacts without re-entering your details.', {
        duration: 5000
      });
    }
  };

  const handleDownloadAll = async () => {
    toast.info(`Downloading ${group.contacts.length} vCards...`);
    for (let i = 0; i < group.contacts.length; i++) {
      await handleDownloadVCard(i, group.contacts[i].name);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">Loading group...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-center">
          <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Group Not Found</h2>
          <p className="text-slate-400">This contact group doesn't exist or has been deleted.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mx-auto mb-4">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {group.name}
          </h1>
          {group.description && (
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">{group.description}</p>
          )}
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700">
            <Users className="w-4 h-4 text-cyan-400" />
            <span className="text-slate-300">{group.contacts?.length || 0} Contacts</span>
          </div>
        </div>

        {/* Created By */}
        {group.createdBy && group.createdBy.name && (
          <Card className="p-4 bg-slate-800/30 backdrop-blur-sm border border-slate-700 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Shared by</p>
                <p className="text-white font-semibold">{group.createdBy.name}</p>
                {group.createdBy.email && (
                  <p className="text-xs text-slate-500">{group.createdBy.email}</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Download All Button - REMOVED as per user request */}

        {/* Contacts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {group.contacts && group.contacts.length > 0 ? (
            group.contacts.map((contact, index) => (
              <Card key={index} className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 hover:border-cyan-500 transition-all">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-white mb-1">{contact.name}</h3>
                  {contact.businessName && (
                    <p className="text-cyan-400 font-semibold">{contact.businessName}</p>
                  )}
                  {contact.title && (
                    <p className="text-slate-400 text-sm">{contact.title}</p>
                  )}
                </div>

                <div className="space-y-2 mb-6">
                  {contact.email && (
                    <div className="flex items-start gap-2 text-sm">
                      <Mail className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <a href={`mailto:${contact.email}`} className="text-slate-300 hover:text-cyan-400 transition-colors break-all">
                        {contact.email}
                      </a>
                    </div>
                  )}
                  
                  {contact.phone && (
                    <div className="flex items-start gap-2 text-sm">
                      <Phone className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <a href={`tel:${contact.phone}`} className="text-slate-300 hover:text-cyan-400 transition-colors">
                        {contact.phone}
                      </a>
                    </div>
                  )}

                  {contact.website && (
                    <div className="flex items-start gap-2 text-sm">
                      <Globe className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-cyan-400 transition-colors break-all">
                        {contact.website}
                      </a>
                    </div>
                  )}

                  {contact.address && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{contact.address}</span>
                    </div>
                  )}

                  {contact.linkedin && (
                    <div className="flex items-start gap-2 text-sm">
                      <Linkedin className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-cyan-400 transition-colors break-all">
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => handleDownloadVCard(index, contact.name)}
                  disabled={downloadingIndex === index}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  {downloadingIndex === index ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 animate-pulse" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download vCard
                    </>
                  )}
                </Button>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No contacts in this group</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            Powered by DigiConnect
          </p>
        </div>
      </div>

      {/* Contact Download Modal for non-logged-in users */}
      <ContactDownloadModal
        isOpen={showDownloadModal}
        onClose={() => {
          setShowDownloadModal(false);
          setPendingDownload(null);
        }}
        onSubmit={handleModalSubmit}
        contactOwnerName={group?.name || 'this group'}
      />
    </div>
  );
};

export default GroupView;
